import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextNew';
import { authAPI } from '../../services/api';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Email,
  ArrowBack
} from '@mui/icons-material';


const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // Use the authAPI directly for better error handling
      const response = await authAPI.forgotPassword(email);
      
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.error || error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A2A66 0%, #1a3a76 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Shape */}
      <Box
        sx={{
          position: 'absolute',
          top: 40,
          left: 16,
          width: 500,
          height: '60vw',
          maxHeight: 400,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
          transform: 'rotate(-5deg)',
          zIndex: 1
        }}
      />

      <Container
        maxWidth="sm"
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: { xs: 8, md: 12 },
          pb: 4
        }}
      >
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToLogin}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                color: '#006AF9',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(0, 106, 249, 0.1)'
                }
              }}
            >
              Back to Login
            </Button>
            
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: '#00040B',
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2rem' }
              }}
            >
              Forgot Password?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              Enter your email address and we'll send you a link to reset your password
            </Typography>
          </Box>

          {/* Error/Success Messages */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: '#d32f2f'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: '#2e7d32'
                }
              }}
            >
              Password reset email sent! Please check your inbox and follow the instructions to reset your password.
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
              placeholder="Enter your email address"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="email"
              disabled={success}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#006AF9' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || success}
              sx={{
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#006AF9',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#0056cc',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 106, 249, 0.3)'
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Reset Link'
              )}
            </Button>

            {success && (
              <Box sx={{ textAlign: 'center', mt: 3, pt: 3, borderTop: '1px solid #e1e5e9' }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  Didn't receive the email?
                </Typography>
                <Button
                  variant="text"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{
                    color: '#006AF9',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 106, 249, 0.1)'
                    }
                  }}
                >
                  Resend Email
                </Button>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', pt: 3, borderTop: '1px solid #e1e5e9' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Remember your password?{' '}
              <Link 
                to="/login" 
                style={{
                  color: '#006AF9',
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword; 